import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { ArrowRightIcon, Loader2Icon, RefreshCwIcon, XIcon } from "lucide-react";
import { useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

type ButtonState = "disabled" | "search" | "relaunch" | "loading" | "cancel";

const BUTTON_SIZE = "w-12 h-12";
const ICON_SIZE = "w-5 h-5";

export const ButtonSearchCompanies = ({
  isLoading = false,
  isRelaunch = false,
  hasInput = false,
  onCancel = () => {},
  onRelaunch = () => {},
  onSearch = () => {},
}: {
  isLoading?: boolean;
  isRelaunch?: boolean;
  hasInput?: boolean;
  onCancel?: () => void;
  onRelaunch?: () => void;
  onSearch?: () => void;
}) => {
  const { t } = useTranslation();
  const [isHovering, setIsHovering] = useState<boolean>(false);

  const buttonState = useButtonState(isLoading, isHovering, isRelaunch, hasInput);

  const getIcon = (): React.ReactNode => {
    switch (buttonState) {
      case "cancel":
        return <XIcon className={cn("flex-shrink-0", ICON_SIZE)} />;
      case "loading":
        return (
          <Loader2Icon
            className={cn("flex-shrink-0 animate-spin", ICON_SIZE)}
          />
        );
      case "relaunch":
        return <RefreshCwIcon className={cn("flex-shrink-0", ICON_SIZE)} />;
      default:
        return <ArrowRightIcon className={cn("flex-shrink-0", ICON_SIZE)} />;
    }
  };

  const button = (
    <button
      type="button"
      aria-label={buttonState === "cancel" ? t('prospecting.search.buttonCancel') : undefined}
      disabled={buttonState === "disabled"}
      className={cn(
        "flex items-center justify-center rounded-full transition-colors duration-300",
        BUTTON_SIZE,
        {
          "bg-secondary text-secondary-foreground opacity-50 cursor-not-allowed":
            buttonState === "disabled",
          "bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer":
            buttonState === "relaunch" || buttonState === "search",
          "bg-secondary text-secondary-foreground cursor-default":
            buttonState === "loading",
          "bg-red-500 text-white hover:bg-red-600 cursor-pointer":
            buttonState === "cancel",
        },
      )}
      onClick={() => {
        if (buttonState === "cancel") {
          onCancel();
        } else if (buttonState === "relaunch") {
          onRelaunch();
        } else if (buttonState === "search") {
          onSearch();
        }
      }}
    >
      {getIcon()}
    </button>
  );

  return (
    <motion.div
      className="overflow-hidden mr-2"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {isLoading || isRelaunch ? (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent>
              {isLoading
                ? t("prospecting.search.buttonCancel")
                : t("prospecting.search.buttonRelaunch")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        button
      )}
    </motion.div>
  );
};

const useButtonState = (
  isLoading: boolean,
  isHovering: boolean,
  isRelaunch: boolean,
  hasInput: boolean,
): ButtonState => {
  if (isLoading && isHovering) return "cancel";
  if (isLoading) return "loading";
  if (isRelaunch) return "relaunch";
  if (hasInput) return "search";
  return "disabled";
};
